import React, { useState, useEffect } from 'react'
import CancellationReasonForm from '../components/CancellationReasonForm'
import { apiService } from '../services/apiService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const CancellationReasonsPage = () => {
  const [reasons, setReasons] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingReason, setEditingReason] = useState(null)

  useEffect(() => {
    loadReasons()
  }, [])

  const loadReasons = async () => {
    try {
      setLoading(true)
      const result = await apiService.getCancellationReasons()
      setReasons(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar motivos de cancelamento:', error)
      setReasons([])
      toast.error('Erro ao carregar motivos de cancelamento')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingReason(null)
    setShowForm(true)
  }

  const handleEdit = (reason) => {
    setEditingReason(reason)
    setShowForm(true)
  }

  const handleView = (reason) => {
    toast(`Visualizando motivo: ${reason.name}`, {
      icon: 'ℹ️',
    })
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`Tem certeza que deseja desativar o motivo de cancelamento "${name}"?`)) {
      return
    }

    try {
      await apiService.deleteCancellationReason(id)
      toast.success('Motivo de cancelamento desativado com sucesso!')
      loadReasons()
    } catch (error) {
      console.error('Erro ao desativar motivo de cancelamento:', error)
      toast.error('Erro ao desativar motivo de cancelamento')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingReason(null)
  }

  const handleFormSuccess = () => {
    loadReasons()
    setShowForm(false)
    setEditingReason(null)
  }

  const filteredReasons = reasons.filter(reason => {
    const searchLower = searchQuery.toLowerCase()
    return (
      reason.name.toLowerCase().includes(searchLower) ||
      reason.applies_to.toLowerCase().includes(searchLower) ||
      (reason.description && reason.description.toLowerCase().includes(searchLower))
    )
  })

  const getAppliesToColor = (appliesTo) => {
    const colors = {
      'contract': 'bg-blue-100 text-blue-800',
      'payment': 'bg-green-100 text-green-800',
      'service': 'bg-yellow-100 text-yellow-800',
      'maintenance': 'bg-purple-100 text-purple-800'
    }
    return colors[appliesTo] || 'bg-gray-100 text-gray-800'
  }

  const getAppliesToLabel = (appliesTo) => {
    const labels = {
      'contract': 'Contrato',
      'payment': 'Pagamento',
      'service': 'Serviço',
      'maintenance': 'Manutenção'
    }
    return labels[appliesTo] || appliesTo
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando motivos de cancelamento...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <XCircleIcon className="h-8 w-8 text-blue-600 mr-3" />
            Motivos de Cancelamento
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie os motivos de cancelamento do sistema
          </p>
        </div>

        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Novo Motivo</span>
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome, aplicação ou descrição..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {filteredReasons.length === 0 ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhum motivo encontrado' : 'Backend implementado'}
            </h3>
            <p className="text-gray-500 mb-4">
              API funcionando. Frontend completo em desenvolvimento.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aplica-se a
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requer Aprovação
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReasons.map((reason) => (
                  <tr key={reason.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {reason.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAppliesToColor(reason.applies_to)}`}>
                        {getAppliesToLabel(reason.applies_to)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {reason.requires_approval ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          Sim
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Não
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {reason.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(reason)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(reason)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(reason.id, reason.name)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Desativar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal do Formulário */}
      {showForm && (
        <CancellationReasonForm
          cancellationReason={editingReason}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}

export default CancellationReasonsPage